import React, { useState, useEffect } from 'react';
import { 
  collection, 
  getDocs, 
  doc, 
  setDoc, 
  deleteDoc, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import { toast } from 'react-toastify';
import { 
  UserAddIcon, 
  TrashIcon, 
  ShieldCheckIcon, 
  UserIcon,
  XIcon
} from '@heroicons/react/outline';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newRole, setNewRole] = useState("author");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, "users"));
      const userList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUsers(userList);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Gagal mengambil data user");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleAddUser = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      // We store user roles in Firestore keyed by email
      await setDoc(doc(db, "users", newEmail), {
        email: newEmail,
        role: newRole,
        createdAt: serverTimestamp()
      });
      
      toast.success(`User ${newEmail} berhasil ditambahkan!`);
      setIsModalOpen(false);
      setNewEmail("");
      fetchUsers();
    } catch (error) {
      console.error("Error adding user:", error);
      toast.error("Gagal menambah user");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteUser = async (email) => {
    if (!window.confirm(`Hapus akses untuk ${email}?`)) return;

    try {
      await deleteDoc(doc(db, "users", email));
      toast.success("User berhasil dihapus");
      fetchUsers();
    } catch (error) {
      console.error("Error deleting user:", error);
      toast.error("Gagal menghapus user");
    }
  };

  return (
    <div className="p-6 sm:p-10 font-sans">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">Manajemen User</h1>
          <p className="text-sm text-gray-500 mt-1">Kelola admin gampong dan hak akses mereka</p>
        </div>
        <button 
          onClick={() => setIsModalOpen(true)}
          className="inline-flex items-center px-5 py-2.5 bg-primary text-white rounded-2xl text-sm font-bold hover:bg-primary/95 transition-all shadow-lg shadow-primary/20"
        >
          <UserAddIcon className="w-5 h-5 mr-2" />
          Tambah User
        </button>
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100">
            <thead className="bg-gray-50/50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">User / Email</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Role</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Dibuat Pada</th>
                <th className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Aksi</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan="4" className="px-6 py-10 text-center text-gray-400">Loading users...</td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-10 text-center text-gray-400">Belum ada user tambahan.</td>
                </tr>
              ) : users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50/50 transition-colors group">
                  <td className="px-6 py-5 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-9 w-9 bg-gray-100 rounded-full flex items-center justify-center mr-3">
                        <UserIcon className="w-5 h-5 text-gray-400" />
                      </div>
                      <div className="text-sm font-bold text-gray-900">{user.email}</div>
                    </div>
                  </td>
                  <td className="px-6 py-5 whitespace-nowrap">
                    <span className={`px-3 py-1 inline-flex text-xs leading-5 font-bold rounded-full ${
                      user.role === 'superadmin' ? 'bg-purple-50 text-purple-700' : 'bg-blue-50 text-blue-700'
                    }`}>
                      {user.role}
                    </span>
                  </td>
                  <td className="px-6 py-5 whitespace-nowrap text-sm text-gray-500">
                    {user.createdAt?.toDate()?.toLocaleDateString() || '-'}
                  </td>
                  <td className="px-6 py-5 whitespace-nowrap text-right text-sm">
                    <button 
                      onClick={() => handleDeleteUser(user.email)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Hapus Akses"
                    >
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add User Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" onClick={() => setIsModalOpen(false)}></div>
          <div className="relative bg-white w-full max-w-md rounded-3xl shadow-2xl overflow-hidden animate-modalIn">
            <div className="px-8 py-6 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
              <h3 className="text-xl font-extrabold text-gray-900 tracking-tight flex items-center">
                <ShieldCheckIcon className="w-6 h-6 mr-2 text-primary" />
                Tambah User Akses
              </h3>
              <button onClick={() => setIsModalOpen(false)} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
                <XIcon className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleAddUser} className="p-8 space-y-6">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1.5">Email Pengguna</label>
                <input 
                  type="email" 
                  required
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  className="block w-full px-4 py-3.5 border border-gray-100 bg-gray-50/50 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                  placeholder="name@gampong.id"
                />
                <p className="mt-2 text-[11px] text-gray-400 italic">Pastikan user sudah melakukan registrasi di Firebase Auth.</p>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1.5">Role / Hak Akses</label>
                <select 
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value)}
                  className="block w-full px-4 py-3.5 border border-gray-100 bg-gray-50/50 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all appearance-none bg-no-repeat bg-[right_1rem_center]"
                >
                  <option value="author">Author (Hanya Kelola Konten)</option>
                  <option value="superadmin">Superadmin (Kelola User & Konten)</option>
                </select>
              </div>

              <div className="flex space-x-3 pt-2">
                <button type="button" onClick={() => setIsModalOpen(false)} className="flex-1 px-4 py-3.5 border border-gray-100 text-gray-600 text-sm font-bold rounded-2xl hover:bg-gray-50 transition-colors">
                  Batal
                </button>
                <button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="flex-[2] px-4 py-3.5 bg-primary text-white text-sm font-bold rounded-2xl hover:bg-primary/95 transition-all shadow-lg shadow-primary/20 disabled:opacity-50"
                >
                  {isSubmitting ? "Menyimpan..." : "Simpan Hak Akses"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
